import Router from 'next/router';
import { useCallback } from 'react';
import { useDispatch } from 'react-redux';

import { type OpenAssetModalLocations, useTrackOpenedAssetModal } from '@air/analytics';
import { ClipType } from '@air/api/types';

import { calculateRankForItem } from '~/analytics/rankUtils';
import { QueryParamNames, type QueryType } from '~/constants/search';
import { setCentralizedClipAction } from '~/store/centralizedClip/actions';
import { pushWithExistingQuery } from '~/utils/PathUtils';
import { useAirStore } from '~/utils/ReduxUtils';

export type GoToAssetPageProps = {
  asset: Parameters<typeof setCentralizedClipAction>[0]['clip'];
  base?: string;
  query?: QueryType;
  trackLocation?: OpenAssetModalLocations;
  isAssetModalAlreadyOpen?: boolean;
};

const getQueryParamTimestamp = (asset: GoToAssetPageProps['asset']): string | null =>
  typeof asset.assets?.spriteSheet !== 'undefined' &&
  asset.type === ClipType.video &&
  asset.position?.start.timestamp !== undefined
    ? String(asset.position.start.timestamp)
    : null;

export function useGoToAssetPage() {
  const dispatch = useDispatch();
  const store = useAirStore();
  const { trackOpenedAssetModal } = useTrackOpenedAssetModal();

  const goToAssetPage = useCallback(
    ({
      asset,
      query,
      base = Router.asPath,
      trackLocation = 'not-set',
      isAssetModalAlreadyOpen = false,
    }: GoToAssetPageProps) => {
      Router.ready(() => {
        /**
         * Split away any query params
         * Split away a previous clip id that might be there
         */
        const basePath = base.split('?')[0].split('/c/')[0];
        const assetPath = `/c/${asset.id}`;
        const path = `${basePath}${assetPath}`;

        if (Router.asPath.includes(asset.id)) return;

        dispatch(
          setCentralizedClipAction({
            clip: asset,
          }),
        );

        pushWithExistingQuery({
          path,
          newQuery: {
            [QueryParamNames.notificationsPanel]: null,
            [QueryParamNames.timestamp]: getQueryParamTimestamp(asset),
            [QueryParamNames.discussionId]: null,
            [QueryParamNames.page]: null,
            [QueryParamNames.faceId]: null,
            ...query,
          },
        });

        if (!isAssetModalAlreadyOpen) {
          const rank = calculateRankForItem(store, asset.id);

          trackOpenedAssetModal({
            location: trackLocation,
            ...(rank !== undefined && { rank }),
          });
        }
      });
    },
    [dispatch, store, trackOpenedAssetModal],
  );

  return {
    goToAssetPage,
  };
}
